import { Component, inject, input, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsUpdate } from '../../../../forms/ziro-forms-models';
import { Observable, Subject } from 'rxjs';
import { ModelProtocolFieldConfig } from '../../../../shared/models/generated/ziroModels';
import { PhoneType } from '../../../../shared/models/service-type';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { KnownPhoneModels } from '../../../shared/contexts/user-detail-ui.context';
import { ActivatedRoute, Router } from '@angular/router';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { filter, first } from 'rxjs/operators';
import { PhoneUiContext } from '../../../../shared/phone-buttons/contexts/phone-ui.context';
import { outputFromObservable } from '@angular/core/rxjs-interop';

export interface ModelProtocolFormData {
  model: string;
  protocol: string;
}

@Component({
  selector: 'ziro-phone-model-protocol-form',
  templateUrl: './phone-model-protocol-form.component.html',
  providers: [{ provide: ZiroFormAbstractDirective, useExisting: PhoneModelProtocolFormComponent }],
  standalone: false,
})
export class PhoneModelProtocolFormComponent
  extends ZiroFormAbstractDirective<ModelProtocolFormData>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private phoneUiContext = inject(PhoneUiContext);

  readonly phoneType = input<PhoneType>();
  readonly fieldConfig = input<ModelProtocolFieldConfig>();
  readonly isGeneratingPhone = input<boolean>();

  private _modelProtocolUpdateSource = new Subject<ZiroFormsUpdate<ModelProtocolFormData>>();
  readonly modelProtocolUpdate$ = outputFromObservable(this._modelProtocolUpdateSource.asObservable());

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    const isExisting = this.route.snapshot.params.phoneId && !this.router.url.endsWith('copy');
    this.setIsExisting(isExisting);
    this._initFormConfig();

    this.ziroFormsUpdate$.subscribe((update) => {
      if (update.new.model !== update.old?.model || (update.new.model && !update.new.protocol)) {
        this._setProtocolConfig(update.new);
      }
      if (update.new.model && update.new.protocol) {
        this._modelProtocolUpdateSource.next(update);
      }
    });
  }

  submit(): Observable<boolean> {
    return this.phoneUiContext.isSaving$.pipe(
      filter((isSaving) => !isSaving),
      first(),
    );
  }

  private _initFormConfig(): void {
    const protocolConfig = this.fieldConfig().modelSpecificProtocols[this.entity.model];
    const protocolPossibleOptions = protocolConfig?.possibleOptions || [];

    this.formConfig = {
      fields: {
        model: {
          label: 'tkey;pages.details.phoneSettings.model',
          dataAutomation: 'phone-model-select',
          componentConfig: new ZiroSelectConfig({
            options: this.fieldConfig().models.possibleOptions,
          }),
          defaultValue: () => {
            const phoneType = this.phoneType();
            return phoneType === PhoneType.DESKPHONE || phoneType === PhoneType.EXTENSION_MOBILITY
              ? this.fieldConfig().models.defaultValue
              : KnownPhoneModels[phoneType?.toUpperCase().replace(' ', '_') as keyof typeof KnownPhoneModels];
          },
          required: this.fieldConfig().models.required,
          hidden: () => !this.fieldConfig().models.show,
          disabled: () => this.isGeneratingPhone() || this.isSubmitting,
        },
        protocol: {
          label: 'tkey;pages.details.phoneSettings.protocol.label',
          dataAutomation: 'phone-protocol-select',
          componentConfig: new ZiroSelectConfig({ options: protocolPossibleOptions }),
          required: true,
          hidden: () => {
            const protocolConfig = this.fieldConfig().modelSpecificProtocols[this.formData?.model];
            return !protocolConfig || !protocolConfig.show;
          },
          disabled: () => this.isGeneratingPhone() || this.isSubmitting,
        },
      },
    };
  }

  private _setProtocolConfig(entity: ModelProtocolFormData): void {
    const protocolConfig = this.fieldConfig().modelSpecificProtocols[entity.model];
    const protocolPossibleOptions = protocolConfig?.possibleOptions || [];
    const oldProtocol = entity.protocol;

    if (protocolPossibleOptions.length === 1) {
      entity.protocol = protocolPossibleOptions[0];
    } else if (protocolPossibleOptions.length === 2 && (!this.entity.protocol || protocolConfig.defaultValue === '')) {
      entity.protocol = protocolConfig.defaultValue;
    }

    const protocolField = this.fieldComponents.find((field) => field.fieldId() === 'protocol');
    protocolField.applyComponentConfig(new ZiroSelectConfig({ options: protocolPossibleOptions }));
    if (entity.protocol !== oldProtocol) {
      const protocolChannel = this.fieldChannels['protocol'];
      protocolChannel.entitySource.next(entity.protocol);
      protocolChannel.validateSource.next();
    }
  }
}
